import { DeleteFilled } from "@ant-design/icons";
import { getCorrectHourStatus, truncateFloat } from "./generalHelper";
import { InmersysRoot, ProjectRoot } from "../router/type_router";
import { statusProjectType } from "../types/statusProject";
import { getRequestProjectTableClickup } from "../config";
import { colorConsumeTags } from "./checkFilters";
import { Clipunlink } from "../components/icons";
import { createAdminName } from "./userHelpers";
import { replaceIdURL } from "./replaceId";

const calculatePercentages = (projects) => {
    const percentages = {
        progress: 0,
        acceptable: 0,
        attention: 0,
        extended: 0,
        overflow: 0,
    };

    projects.forEach(({ percentage }) => {
        const correctPercentage = getCorrectHourStatus(percentage);
        if (!correctPercentage) return;
        percentages[correctPercentage.label] += 1;
    });

    return percentages;
};

const updateStatus = (projects) => {
    const updatedStatus = {
        CONCLUIDO: 0,
        ACTIVO: 0,
        PAUSA: 0,
        CANCELADO: 0,
    };

    projects.forEach(({ status }) => {
        updatedStatus[status]++;
    });

    return updatedStatus;
};

const getHoursWorkAndBudget = (projects) => {
    let workedHours = 0;
    let budgetHours = 0;

    projects.forEach(({ calculatedHours, timeGlobalTotal }) => {
        budgetHours += calculatedHours.total;
        workedHours += timeGlobalTotal;
    });

    return {
        workedHours,
        budgetHours,
    };
};

export const geCardsForProjectTable = (projects = []) => {
    const hours = getHoursWorkAndBudget(projects);
    const percentages = calculatePercentages(projects);
    const updatedStatus = updateStatus(projects);

    return {
        ...updatedStatus,
        ...percentages,
        ...hours,
    };
};

export const fetchData = async (isOwn, year) => {
    try {
        const { payload } = await getRequestProjectTableClickup(isOwn, year);
        const { spaces, adminTeam } = payload;
        const { peopleInCharge, peopleInChargeProject, leaders } = adminTeam;

        const cardData = geCardsForProjectTable(spaces);

        return { spaces, cardData, peopleInCharge, peopleInChargeProject, leaders };
    } catch (error) {
        throw new Error("Error al recuperar proyectos");
    }
};

export const createColumn = ({
    peopleInCharge,
    peopleInChargeProject,
    leaders,
    filtersTable = {
        commercial: [],
        leader: [],
        responsible: [],
        status: [],
    },
    statusImageFormatter,
    isEdit = false,
    actionDeleteProyect = () => { }
}) => [
        {
            title: "%",
            dataIndex: "percentage",
            render: (percentage) => <>{colorConsumeTags(percentage)}</>,
            sorter: (a, b) => a.percentage - b.percentage,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (_, record) => {
                const urlId = replaceIdURL(ProjectRoot.selectproject, "id", record.clickupSpace);
                return (
                    <a href={`${InmersysRoot.root}/${InmersysRoot.project}/${urlId}`} target="_blank" rel="noreferrer">
                        <span>{record.name}</span>
                    </a>
                );
            },
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Hrs presupuesto",
            dataIndex: "estimatedTime",
            render: (_, record) => <>{truncateFloat(record.calculatedHours.total)}</>,
        },
        {
            title: "Hrs trabajadas",
            dataIndex: "timeGlobalTotal",
            render: (hours) => <>{truncateFloat(hours)}</>,
        },
        {
            title: "Hrs diferencia",
            sorter: (a, b) => a.calculatedHours.total - a.timeGlobalTotal - (b.calculatedHours.total - b.timeGlobalTotal),
            render: (_, element) => <>{truncateFloat(element.calculatedHours.total - element.timeGlobalTotal)}</>,
        },
        {
            title: "Comercial",
            dataIndex: "commercial",
            filteredValue: filtersTable?.commercial ?? [],
            filters: peopleInChargeProject.users.map((user) => ({ text: user.name, value: user.name })),
            onFilter: (value, record) => record.teamAdmin.peopleInChargeProject?.some((user) => user.name === value),
            render: (_, record) => record.teamAdmin.peopleInChargeProject.map((person) => createAdminName(person.name)).join(" ,"),
        },
        {
            title: "Líder",
            dataIndex: "leader",
            filteredValue: filtersTable?.leader ?? [],
            filters: leaders.users.map((user) => ({ text: user.name, value: user.name })),
            onFilter: (value, record) => record.teamAdmin.leaders?.some((user) => user.name === value),
            render: (_, record) => record.teamAdmin.leaders.map((person) => createAdminName(person.name)).join(" ,"),
        },
        {
            title: "Responsable",
            dataIndex: "responsible",
            filteredValue: filtersTable?.responsible ?? [],
            filters: peopleInCharge.users.map((user) => ({ text: user.name, value: user.name })),
            onFilter: (value, record) => record.teamAdmin.peopleInCharge?.some((user) => user.name === value),
            render: (_, record) => record.teamAdmin.peopleInCharge.map((person) => createAdminName(person.name)).join(" ,"),
        },
        {
            title: "Estado",
            dataIndex: "status",
            filteredValue: filtersTable?.status ?? [],
            render: (text, record) => statusImageFormatter(text, record.hasComments),
            filters: [
                { text: "Concluido", value: "concluido" },
                { text: "Activo", value: "activo" },
                { text: "Pausa", value: "pausa" },
                { text: "Cancelado", value: "cancelado" },
            ],
            onFilter: (value, record) => record.status.toLowerCase() === value,
        },
        {
            title: "Acciones",
            dataIndex: "actions",
            render: (_, record) => {
                if (record.isDeleted) return null
                if (!isEdit) return null
                const canUnlink = [statusProjectType.concluid, statusProjectType.cancel].includes(record.status)
                const canDelete = record.status === statusProjectType.active
                return (
                    <div className="flex gap-4 items-center justify-center">
                        {canUnlink && (
                            <Clipunlink
                                onClick={() => {
                                    actionDeleteProyect({ active: true, clickupSpace: record.clickupSpace, name: record.name, type: "unlink" })
                                }}
                            />
                        )}

                        {canDelete && (
                            <DeleteFilled
                                onClick={() => {
                                    actionDeleteProyect({ active: true, clickupSpace: record.clickupSpace, name: record.name, type: "delete" })
                                }}
                                className="cursor-pointer text-red-700 text-lg"
                            />
                        )}
                    </div>
                )
            }
        },
    ];
